import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { STATUS_STYLES, shortDate } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCcw } from "lucide-react";

const STATUSES = ["Active", "Inactive", "No Sale This Month", "Follow-up Required", "New Customer", "Returning Customer"];

export default function CustomerActivity() {
  const [customers, setCustomers] = useState([]);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/customers", { params: status ? { status } : {} }).then(r => setCustomers(r.data));
  useEffect(() => { load(); }, [status]);

  const recompute = async () => {
    setBusy(true);
    try { await api.post("/customers/recompute-status"); toast.success("Statuses recomputed"); load(); }
    catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const counts = STATUSES.map(s => ({ status: s, count: customers.filter(c => c.status === s).length }));

  return (
    <div className="space-y-6" data-testid="activity-page">
      <div className="flex justify-between items-end gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Customer Activity</h1>
          <p className="text-slate-600 mt-1">Automated classifications based on order history</p>
        </div>
        <button onClick={recompute} disabled={busy} className="h-11 px-5 border border-slate-300 rounded-lg font-semibold flex items-center gap-2 hover:border-[#003DA5]" data-testid="recompute-btn">
          <RefreshCcw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Recompute
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {counts.map(c => (
          <button key={c.status} onClick={() => setStatus(status === c.status ? "" : c.status)} className={`text-left rounded-xl border p-4 transition ${status === c.status ? "border-[#003DA5] ring-2 ring-[#003DA5]/20" : "border-slate-200 bg-white"}`} data-testid={`filter-${c.status}`}>
            <div className={`text-[10px] px-2 py-1 rounded-full inline-block font-semibold ${STATUS_STYLES[c.status] || "badge-inactive"}`}>{c.status}</div>
            <div className="kpi-value text-2xl mt-2">{c.count}</div>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
              <th className="text-left px-4 py-3">Account #</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Town</th>
              <th className="text-left px-4 py-3">State</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Last Order</th>
            </tr></thead>
            <tbody>{customers.map(c => (
              <tr key={c.id} className="border-t hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-xs">{c.account_number}</td>
                <td className="px-4 py-3"><Link to={`/customers/${c.id}`} className="font-semibold hover:text-[#003DA5]">{c.name}</Link></td>
                <td className="px-4 py-3">{c.town}</td>
                <td className="px-4 py-3">{c.state}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${STATUS_STYLES[c.status] || "badge-inactive"}`}>{c.status}</span></td>
                <td className="px-4 py-3">{shortDate(c.last_order_date)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <div className="md:hidden divide-y">
          {customers.map(c => (
            <Link key={c.id} to={`/customers/${c.id}`} className="block p-3 hover:bg-slate-50">
              <div className="flex justify-between"><span className="font-semibold">{c.name}</span><span className={`text-[10px] px-2 py-1 rounded-full ${STATUS_STYLES[c.status]}`}>{c.status}</span></div>
              <div className="text-xs text-slate-500">{c.town}, {c.state} • Last order {shortDate(c.last_order_date)}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
